import React, { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { renderAsync } from 'docx-preview';
import type {
  createReport as createReportFunction,
  listCommands as listCommandsFunction,
} from 'docx-templates';
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  ChartData,
  ChartType,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PieController,
  PointElement,
  Tooltip,
} from 'chart.js';
import { TemplateObjectUtils } from '../../../utils/template-object';

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  PieController,
  ArcElement,
  LineController,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

type BrowserDocxTemplates = {
  createReport: typeof createReportFunction;
  listCommands: typeof listCommandsFunction;
};

interface TemplateTesterProps {
  initialTemplate?: Blob;
  templateName?: string;
  templateKey?: string;
  initialData?: string;
  showTemplateUpload?: boolean;
}

interface TemplateSource {
  blob: Blob;
  name: string;
}

const docxPreviewOptions = {
  breakPages: true,
  ignoreFonts: true,
  renderAltChunks: false,
  renderComments: false,
  renderChanges: false,
  renderEndnotes: true,
  renderFooters: true,
  renderHeaders: true,
  renderFootnotes: true,
  useBase64URL: true,
};
const emptyTemplateData = '{\n  \n}';
const maxTemplateSize = 10 * 1024 * 1024;
const maxJsonSize = 1024 * 1024;
const docxTemplatesBrowserModuleUrl = `${process.env.PUBLIC_URL}/vendor/docx-templates.browser.js`;

async function loadDocxTemplates(): Promise<BrowserDocxTemplates> {
  return (await import(
    /* webpackIgnore: true */ docxTemplatesBrowserModuleUrl
  )) as BrowserDocxTemplates;
}

function formatTemplateData(templateData?: string) {
  if (!templateData) return emptyTemplateData;

  try {
    return JSON.stringify(JSON.parse(templateData), null, 2);
  } catch {
    return templateData;
  }
}

function hardenPreviewLinks(previewContainer: HTMLDivElement) {
  previewContainer.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
    try {
      const url = new URL(link.href, window.location.origin);
      if (!['http:', 'https:', 'mailto:'].includes(url.protocol)) {
        link.removeAttribute('href');
        return;
      }

      link.target = '_blank';
      link.rel = 'noopener noreferrer';
    } catch {
      link.removeAttribute('href');
    }
  });
}

function fitPreviewToWidth(previewContainer: HTMLDivElement) {
  previewContainer.querySelectorAll<HTMLElement>('.docx').forEach((page) => {
    const wrapper = page.closest<HTMLElement>('.docx-wrapper');
    if (!wrapper) return;

    const wrapperStyle = window.getComputedStyle(wrapper);
    const horizontalPadding =
      Number.parseFloat(wrapperStyle.paddingLeft) + Number.parseFloat(wrapperStyle.paddingRight);
    const availableWidth = Math.max(wrapper.clientWidth - horizontalPadding, 0);

    page.style.removeProperty('zoom');
    const pageWidth = page.offsetWidth;
    if (pageWidth > availableWidth) {
      page.style.setProperty('zoom', String(availableWidth / pageWidth));
    }
  });
}

async function renderPreview(
  file: Blob,
  container: HTMLDivElement,
  isCurrent: () => boolean,
): Promise<boolean> {
  const stagingContainer = document.createElement('div');
  await renderAsync(file, stagingContainer, stagingContainer, docxPreviewOptions);

  if (!isCurrent()) return false;

  container.replaceChildren(...Array.from(stagingContainer.childNodes));
  fitPreviewToWidth(container);
  hardenPreviewLinks(container);
  return true;
}

function getGenerationErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;

  if (Array.isArray(error)) {
    return error.map(getGenerationErrorMessage).filter(Boolean).join('\n');
  }

  if (typeof error === 'object' && error !== null) {
    const errorWithDetails = error as { message?: unknown; errors?: unknown };
    if (typeof errorWithDetails.message === 'string') return errorWithDetails.message;
    if (Array.isArray(errorWithDetails.errors)) {
      return errorWithDetails.errors.map(getGenerationErrorMessage).filter(Boolean).join('\n');
    }
  }

  return 'The template could not be generated with the provided data.';
}

function createTemplateHelpers() {
  return {
    readImage: async (width: number, height: number, url: string, extension: string) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Unable to read image: ${response.status}`);
      return { width, height, data: await response.arrayBuffer(), extension };
    },
    drawChart: (type: string, width: number, height: number, data: unknown) => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const chart = new Chart(canvas, {
        type: type as ChartType,
        data: data as ChartData,
      });
      const image = chart.toBase64Image('image/png', 1).split(';base64,').pop() || '';
      chart.destroy();

      return {
        width: (width / 96) * 2.54,
        height: (height / 96) * 2.54,
        data: image,
        extension: '.jpeg',
      };
    },
  };
}

export function TemplateTester({
  initialTemplate,
  templateName,
  templateKey,
  initialData,
  showTemplateUpload = true,
}: TemplateTesterProps) {
  const [templateSource, setTemplateSource] = useState<TemplateSource>();
  const [templateData, setTemplateData] = useState(() => formatTemplateData(initialData));
  const [generatedDocument, setGeneratedDocument] = useState<Blob>();
  const [generationError, setGenerationError] = useState<string>();
  const [templatePreviewError, setTemplatePreviewError] = useState<string>();
  const [templateObjectError, setTemplateObjectError] = useState<string>();
  const [isGenerating, setIsGenerating] = useState(false);
  const templatePreviewRef = useRef<HTMLDivElement>(null);
  const generatedPreviewRef = useRef<HTMLDivElement>(null);
  const templatePreviewVersion = useRef(0);
  const templateObjectVersion = useRef(0);
  const generatedPreviewVersion = useRef(0);
  const generationVersion = useRef(0);

  const resetForTemplate = (source?: TemplateSource, data = emptyTemplateData) => {
    generationVersion.current += 1;
    templatePreviewVersion.current += 1;
    templateObjectVersion.current += 1;
    generatedPreviewVersion.current += 1;
    templatePreviewRef.current?.replaceChildren();
    generatedPreviewRef.current?.replaceChildren();
    setTemplateSource(source);
    setTemplateData(data);
    setGeneratedDocument(undefined);
    setGenerationError(undefined);
    setTemplatePreviewError(undefined);
    setTemplateObjectError(undefined);
    setIsGenerating(false);
  };

  useEffect(() => {
    if (initialTemplate) {
      resetForTemplate(
        {
          blob: initialTemplate,
          name: templateName || 'template.docx',
        },
        formatTemplateData(initialData),
      );
    }
  }, [initialData, initialTemplate, templateKey, templateName]);

  useEffect(
    () => () => {
      generationVersion.current += 1;
      templatePreviewVersion.current += 1;
      templateObjectVersion.current += 1;
      generatedPreviewVersion.current += 1;
    },
    [],
  );

  const populateTemplateData = async (file: Blob, version: number) => {
    try {
      const { listCommands } = await loadDocxTemplates();
      const commands = await listCommands(await file.arrayBuffer(), ['{{', '}}']);

      if (version === templateObjectVersion.current) {
        setTemplateData(
          JSON.stringify(TemplateObjectUtils.buildObjectFromCommands(commands), null, 2),
        );
      }
    } catch {
      if (version === templateObjectVersion.current) {
        setTemplateObjectError('The uploaded document could not be analyzed for JSON data.');
      }
    }
  };

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxFiles: 1,
    maxSize: maxTemplateSize,
    multiple: false,
    onDropAccepted: ([file]) => {
      resetForTemplate({ blob: file, name: file.name });
      const version = templateObjectVersion.current;
      void populateTemplateData(file, version);
    },
    onDropRejected: () =>
      setTemplatePreviewError('Upload one .docx template no larger than 10 MB.'),
  });

  useEffect(() => {
    if (!templateSource || !templatePreviewRef.current) return;

    const version = ++templatePreviewVersion.current;
    const previewContainer = templatePreviewRef.current;
    const observer = new ResizeObserver(() => fitPreviewToWidth(previewContainer));
    observer.observe(previewContainer);
    setTemplatePreviewError(undefined);
    previewContainer.replaceChildren();

    renderPreview(
      templateSource.blob,
      previewContainer,
      () => version === templatePreviewVersion.current,
    ).catch(() => {
      if (version === templatePreviewVersion.current) {
        setTemplatePreviewError('The uploaded document could not be previewed.');
      }
    });

    return () => {
      observer.disconnect();
      if (version === templatePreviewVersion.current) templatePreviewVersion.current += 1;
    };
  }, [templateSource]);

  useEffect(() => {
    if (!generatedDocument || !generatedPreviewRef.current) return;

    const version = ++generatedPreviewVersion.current;
    const previewContainer = generatedPreviewRef.current;
    const observer = new ResizeObserver(() => fitPreviewToWidth(previewContainer));
    observer.observe(previewContainer);

    renderPreview(
      generatedDocument,
      previewContainer,
      () => version === generatedPreviewVersion.current,
    ).catch(() => {
      if (version === generatedPreviewVersion.current) {
        setGenerationError('The generated document could not be previewed.');
      }
    });

    return () => {
      observer.disconnect();
      if (version === generatedPreviewVersion.current) generatedPreviewVersion.current += 1;
    };
  }, [generatedDocument]);

  const generateDocument = async () => {
    const requestVersion = ++generationVersion.current;
    generatedPreviewVersion.current += 1;
    generatedPreviewRef.current?.replaceChildren();
    setGeneratedDocument(undefined);
    setGenerationError(undefined);

    if (!templateSource) {
      setGenerationError('Upload a .docx template before generating a document.');
      return;
    }

    if (templateData.length > maxJsonSize) {
      setGenerationError('JSON data must be no larger than 1 MB.');
      return;
    }

    let data: unknown;
    try {
      data = JSON.parse(templateData);
    } catch (error) {
      setGenerationError(`Enter valid JSON data. ${getGenerationErrorMessage(error)}`);
      return;
    }

    setIsGenerating(true);

    try {
      const { createReport } = await loadDocxTemplates();
      const result = await createReport({
        template: new Uint8Array(await templateSource.blob.arrayBuffer()),
        data,
        cmdDelimiter: ['{{', '}}'],
        failFast: false,
        rejectNullish: false,
        additionalJsContext: createTemplateHelpers(),
      });

      if (requestVersion === generationVersion.current) {
        setGeneratedDocument(
          new Blob([result], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          }),
        );
      }
    } catch (error) {
      if (requestVersion === generationVersion.current) {
        setGeneratedDocument(undefined);
        setGenerationError(getGenerationErrorMessage(error));
      }
    } finally {
      if (requestVersion === generationVersion.current) {
        setIsGenerating(false);
      }
    }
  };

  return (
    <div>
      <p className="mb-5 text-sm text-gray-400">
        Template expressions run locally in your browser. Only open templates you trust.
      </p>
      <div className="grid min-h-[48rem] gap-5 lg:grid-cols-3">
        <div className="flex min-h-0 flex-col lg:col-span-2">
          {showTemplateUpload && (
            <>
              <h3 className="mb-2 text-sm font-medium text-white">Template upload</h3>
              <div
                {...getRootProps()}
                className={`step-17 mb-4 cursor-pointer rounded-lg border-2 border-dashed p-5 text-center text-sm ${
                  isDragActive
                    ? 'border-blue-400 bg-blue-950 text-white'
                    : 'border-gray-600 bg-gray-800 text-gray-300'
                }`}
              >
                <input {...getInputProps()} />
                {templateSource
                  ? templateSource.name
                  : 'Drop a .docx template here, or click to select one.'}
              </div>
            </>
          )}
          <div
            ref={templatePreviewRef}
            className="max-h-[66vh] min-h-[24rem] overflow-x-hidden overflow-y-auto bg-gray-200 p-[5px]"
            aria-label="Uploaded template preview"
          />
        </div>
        <div className="flex min-h-0 flex-col">
          <label htmlFor="template-data" className="mb-2 text-sm font-medium text-white">
            JSON data
          </label>
          <textarea
            id="template-data"
            value={templateData}
            onChange={(event) => {
              generationVersion.current += 1;
              templateObjectVersion.current += 1;
              generatedPreviewVersion.current += 1;
              generatedPreviewRef.current?.replaceChildren();
              setTemplateData(event.target.value);
              setGeneratedDocument(undefined);
              setGenerationError(undefined);
              setTemplateObjectError(undefined);
              setIsGenerating(false);
            }}
            spellCheck={false}
            className="min-h-[38rem] flex-1 rounded-lg border border-gray-600 bg-gray-800 p-3 font-mono text-sm text-white focus:border-blue-500 focus:ring-blue-500"
            aria-describedby="template-data-help"
          />
          <p id="template-data-help" className="mt-2 text-sm text-gray-400">
            Use the same JSON data structure your template expects.
          </p>
          <button
            type="button"
            onClick={generateDocument}
            disabled={isGenerating}
            className="mt-4 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isGenerating ? 'Generating…' : 'Generate'}
          </button>
        </div>
      </div>

      {templatePreviewError && (
        <p className="mt-4 whitespace-pre-line text-sm text-red-400">{templatePreviewError}</p>
      )}
      {templateObjectError && (
        <p className="mt-4 whitespace-pre-line text-sm text-red-400">{templateObjectError}</p>
      )}
      {generationError && (
        <p className="mt-4 whitespace-pre-line text-sm text-red-400">{generationError}</p>
      )}

      {generatedDocument && (
        <div className="mt-8">
          <h3 className="mb-3 text-lg font-bold text-white">Generated document preview</h3>
          <div
            ref={generatedPreviewRef}
            className="max-h-[calc(100vh-9rem)] overflow-x-hidden overflow-y-auto bg-gray-200 p-[5px]"
            aria-label="Generated document preview"
          />
        </div>
      )}
    </div>
  );
}
